import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { BUFFET_PRICES, heightLabels } from '../utils/pricing.js';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

export default function TableManagement({ apiUrl }) {
  const { currentBranch, effectiveBranchId, isAllBranches } = useBranch();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();

  // จัดการโต๊ะ = เขียนข้อมูลจริงของสาขาเดียวเสมอ ห้ามเผลอทำตอนเลือก "รวมทุกสาขา"
  // เพราะตอนนั้น effectiveBranchId จะ fallback ไปสาขาแรกตามลำดับตัวอักษร (เคยเกิดเหตุจริง
  // ที่แก้ไขจำนวนลูกค้าโต๊ะของสาขาหนึ่ง แต่ดันไปเขียนทับโต๊ะของอีกสาขาแทน)
  const blocked = isAllBranches || !effectiveBranchId;

  // ✅ เก็บ closedTables (Set) เพื่อ filter ตอน loadTables (ป้องกัน race condition)
  // ใช้ Set เพื่อรองรับการ checkout หลายโต๊ะต่อกัน (offline mode)
  // รองรับทั้งค่าเดี่ยว (ปิดโต๊ะเดียว) และ array (รวมบิลหลายโต๊ะ ปิดพร้อมกันรอบเดียว)
  const closedTableRef = useRef(
    new Set([].concat(location.state?.closedTable || []))
  );

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    seats: 4,
    adults: 0,
    children130: 0,
    children120: 0,
    childrenUnder100: 0,
    zone: 'main',
    status: 'available'
  });
  const [buffetData, setBuffetData] = useState({
    adults: 0,
    childrenBig: 0,
    childrenSmall: 0
  });
  const [showBuffetModal, setShowBuffetModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState({}); // เก็บคำสั่งซื้อของแต่ละโต๊ะ
  const [showEditModal, setShowEditModal] = useState(false); // Modal สำหรับแก้ไขโต๊ะ
  const [editingTableExtras, setEditingTableExtras] = useState([]); // รายการเมนูเพิ่มเงินของโต๊ะที่แก้ไข
  const [editingTableScans, setEditingTableScans] = useState([]); // ประวัติเปิดหน้า QR/เว็บ ของโต๊ะที่แก้ไข
  const [showScanHistory, setShowScanHistory] = useState(false); // พับเก็บประวัติเปิดหน้าไว้ก่อน ค่อยกดดูตอนต้องการ
  const [extraMenuOptions, setExtraMenuOptions] = useState([]); // เมนูที่มีราคา > 0 ไว้เลือกเพิ่มให้โต๊ะ
  const [addExtraMenuItemId, setAddExtraMenuItemId] = useState('');
  // จำนวนคนที่เอารีฟิลของโต๊ะที่กำลังแก้ไข — ลูกค้าเลือกไว้ตอนเปิดโต๊ะ แต่พนักงานต้อง
  // ปรับได้ทีหลังถ้าลูกค้าเปลี่ยนใจระหว่างมื้อ
  const [editRefillQuantity, setEditRefillQuantity] = useState(0);
  // เริ่มที่แท็บ "มีลูกค้า" ถ้าถูกพามาจาก Checkout.jsx ตอนคิดเงินเสร็จ (ดู navigate('/tables', ...)
  // ใน Checkout.jsx) ไม่งั้น default เป็น "ทั้งหมด" ตามเดิม
  const [statusFilter, setStatusFilter] = useState(location.state?.statusFilter || 'all'); // all, occupied, available, nearExpiry
  const [tick, setTick] = useState(0);
  const [showAddTimeModal, setShowAddTimeModal] = useState(false);
  const [addTimeTable, setAddTimeTable] = useState(null);
  const [printingTable, setPrintingTable] = useState(null); // รหัสโต๊ะที่กำลังส่งงานพิมพ์อยู่
  const [scanLimit, setScanLimit] = useState(20); // จำนวนประวัติเปิดหน้าที่ดึงมาแสดงอยู่ตอนนี้
  const [loadingScans, setLoadingScans] = useState(false);
  // รวมบิล — เก็บแค่รหัสโต๊ะหลัก แล้ว lookup สดจาก tables ทุกครั้ง — ถ้าเก็บ
  // ตัว object ไว้เลย ยอดใน modal จะค้างเมื่อลูกค้าสั่งเพิ่มระหว่างเปิด modal ค้างไว้
  // และถ้าโต๊ะถูกปิดจากเครื่องอื่น modal จะปิดตัวเองแทนที่จะพาไปคิดเงินโต๊ะที่ปิดไปแล้ว
  const [mergeSourceCode, setMergeSourceCode] = useState(null);
  const [mergePicks, setMergePicks] = useState([]);
  const mergeSource = mergeSourceCode
    ? tables.find(t => t.code === mergeSourceCode && t.status === 'occupied') || null
    : null;

  // ป้ายส่วนสูงมาจากเกณฑ์ของสาขา ไม่ hard-code — ทุกหน้าต้องตรงกัน
  const HL = heightLabels(BUFFET_PRICES);

  const zones = [
    { value: 'main', label: 'พื้นที่หลัก' },
    { value: 'vip', label: 'ห้อง VIP' },
    { value: 'outdoor', label: 'กลางแจ้ง' },
    { value: 'private', label: 'ห้องส่วนตัว' }
  ];

  const statuses = [
    { value: 'available', label: 'ว่าง' },
    { value: 'occupied', label: 'มีลูกค้า' },
    { value: 'unavailable', label: 'ปิดใช้งาน' }
  ];

  // ยอดที่ต้องจ่ายจริงของโต๊ะ = ค่าบุฟเฟ่ต์ + เมนูที่สั่งเพิ่ม
  // ใช้สูตรค่าบุฟเฟ่ต์เดียวกับ Checkout.jsx และ handlePrintBill — ไม่งั้นยอด
  // ที่พรีวิวใน modal รวมบิลจะไม่ตรงกับยอดที่เก็บจริง
  const tableGrandTotal = (table) =>
    (table.pricing?.totalPrice ?? table.buffet?.total ?? 0) + (tableOrders[table.code] || 0);

  const tableHeadcount = (table) =>
    (table.guests?.adults || 0) + (table.guests?.children130 || 0)
    + (table.guests?.children120 || 0) + (table.guests?.childrenUnder100 || 0);

  // โต๊ะที่ไม่มีทั้งจำนวนคนและยอดเงิน เช็คบิลไม่ได้ — backend ปฏิเสธด้วย
  // emptyCheckoutError ถ้าปล่อยให้ติ๊กรวมบิล จะได้ 400 กลับมา แล้วโต๊ะจะหายจากหน้าจอ
  // ทั้งที่ DB ยัง occupied อยู่ และคิว offline ค้างรายการ failed ถาวร
  const canMergeTable = (table) => tableGrandTotal(table) > 0 || tableHeadcount(table) > 0;

  // โต๊ะอื่นที่เอามารวมบิลด้วยได้ — ต้องมีลูกค้าอยู่ และไม่ใช่โต๊ะตั้งต้นเอง
  const mergeCandidates = () =>
    tables.filter(t => t.status === 'occupied' && t.code !== mergeSource?.code);

  const toggleMergePick = (code) => {
    setMergePicks(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  // ส่งต่อไปหน้าเช็คบิล โดยเลือกโต๊ะหลัก + ติ๊กโต๊ะที่รวมไว้ให้เสร็จ — หน้านั้นเก็บเงินรอบเดียว
  // แต่ยังบันทึก transaction แยกต่อโต๊ะ รายงานยอดต่อโต๊ะจึงไม่เพี้ยน
  const confirmMergeBill = () => {
    // เอาเฉพาะโต๊ะที่ยังมีลูกค้าอยู่จริง — ระหว่างที่ modal เปิดค้าง โต๊ะที่ติ๊กไว้อาจถูกปิดจากเครื่องอื่นไปแล้ว
    const picks = mergePicks.filter(c =>
      tables.some(t => t.code === c && t.status === 'occupied' && canMergeTable(t)));
    if (!mergeSource || picks.length === 0) return;
    const state = { selectedTableCode: mergeSource.code, mergeTableCodes: picks };
    setMergeSourceCode(null);
    setMergePicks([]);
    navigate('/checkout', { state });
  };

  // คำนวณว่าโต๊ะใกล้หมดเวลาหรือไม่ (เหลือน้อยกว่า 15 นาที)
  const isNearExpiry = (table) => {
    if (table.status !== 'occupied' || !table.endTime) return false;
    const now = Date.now();
    const end = new Date(table.endTime).getTime();
    const remaining = end - now;
    return remaining > 0 && remaining <= 15 * 60 * 1000; // 15 นาที
  };

  // นับโต๊ะใกล้หมดเวลา
  const nearExpiryCount = tables.filter(isNearExpiry).length;

  // ✅ Debounce + Request Deduplication: ป้องกันการเรียก loadTables() ซ้ำซ้อน
  const [loadTablesTimeout, setLoadTablesTimeout] = useState(null);
  const isLoadingTablesRef = useRef(false); // ← ref เพื่อให้ deduplication sync ทันที

  const debouncedLoadTables = () => {
    if (loadTablesTimeout) clearTimeout(loadTablesTimeout);
    const timeout = setTimeout(() => {
      loadTables(false);
    }, 500); // รอ 500ms (เร็วพอสำหรับ real-time แต่ป้องกันซ้ำซ้อน)
    setLoadTablesTimeout(timeout);
  };

  // ✅ WebSocket: Reactive state updates - อัปเดตทันทีไม่ต้อง fetch ใหม่
  useSocket(apiUrl, {
    'table:updated': (data) => {
      if (data.tableCode && data.status === 'available') {
        // โต๊ะว่างแล้ว - ลบออก (loadTables จะ clear closedTableRef เมื่อ DB ยืนยัน)
        setTables(prev => prev.filter(t => t.code !== data.tableCode));
        setTableOrders(prev => { const u = { ...prev }; delete u[data.tableCode]; return u; });
      } else if (data.tableCode && data.table) {
        // มีข้อมูลเต็ม - replace
        setTables(prev => prev.map(t => t.code === data.tableCode ? { ...t, ...data.table } : t));
      } else if (data.tableCode && data.updates) {
        // มีข้อมูลบางส่วน - merge
        setTables(prev => prev.map(t => t.code === data.tableCode ? { ...t, ...data.updates } : t));
      }
    },

    'table:closed': (data) => {
      if (data.tableCode) {
        // ลบออกจาก UI ทันที + เก็บใน closedTableRef กัน race condition
        // (ป้องกันโต๊ะกลับมาโผล่ตอน loadTables ดึง cache เก่า)
        closedTableRef.current.add(data.tableCode);
        setTables(prev => prev.filter(t => t.code !== data.tableCode));
        setTableOrders(prev => { const u = { ...prev }; delete u[data.tableCode]; return u; });
      }
    },

    'order:created': (data) => {
      if (data.tableCode && data.order) {
        const total = data.order.items?.reduce((s, i) => s + (i.price > 0 ? i.price * i.quantity : 0), 0) || 0;
        if (total > 0) setTableOrders(prev => ({ ...prev, [data.tableCode]: (prev[data.tableCode] || 0) + total }));
      } else {
        debouncedLoadTables();
      }
    },

    'order:deleted': (data) => {
      if (data.tableCode && data.allDeleted) {
        setTableOrders(prev => ({ ...prev, [data.tableCode]: 0 }));
      } else if (data.tableCode) {
        setTableOrders(prev => ({ ...prev, [data.tableCode]: 0 }));
      } else {
        debouncedLoadTables();
      }
    }
  }, { token: localStorage.getItem('adminToken'), branchId: effectiveBranchId });

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadTables(true);

    // Cleanup timeout on unmount
    return () => {
      if (loadTablesTimeout) clearTimeout(loadTablesTimeout);
    };
  }, [blocked]);

  // ✅ เมื่อ navigate กลับมาจาก checkout - ลบโต๊ะที่เพิ่งปิดออกจาก state ทันที
  useEffect(() => {
    const closedTables = [].concat(location.state?.closedTable || []);
    if (closedTables.length > 0) {
      // Optimistic removal - ลบทันทีไม่ต้องรอ API
      setTables(prev => prev.filter(t => !closedTables.includes(t.code)));
      setTableOrders(prev => {
        const updated = { ...prev };
        closedTables.forEach(code => { delete updated[code]; });
        return updated;
      });

      // เก็บไว้ใน Set เพื่อ filter ตอน loadTables ด้วย (รองรับหลายโต๊ะ)
      closedTables.forEach(code => closedTableRef.current.add(code));

      // Safety: auto-clear filter after 5 min to prevent indefinite hiding
      // (handles edge case where offline sync never processes)
      const safetyTimer = setTimeout(() => {
        closedTables.forEach(code => closedTableRef.current.delete(code));
      }, 5 * 60 * 1000);

      // Clear navigation state
      window.history.replaceState({}, document.title);

      return () => clearTimeout(safetyTimer);
    }
  }, [location.state?.closedTable]);

  // เมนูที่มีราคา > 0 (นอกเหนือบุฟเฟ่ต์ เช่น เบียร์, รีฟิลน้ำ) ไว้ให้เลือกเพิ่มให้โต๊ะ
  // จากหน้าแก้ไขโต๊ะโดยตรง ไม่ต้องให้ลูกค้าสั่งผ่าน QR เอง
  const loadExtraMenuOptions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/menu`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const items = (result.data || []).filter(item => item.price > 0 && item.isAvailable !== false);
      setExtraMenuOptions(items);
    } catch (err) {
      console.error('Failed to load extra menu options:', err);
      setExtraMenuOptions([]);
    }
  };

  const loadTables = async (showLoading = false) => {
    // ✅ Request Deduplication (ใช้ ref เพื่อให้ check sync ทันที ไม่ต้องรอ React state)
    if (isLoadingTablesRef.current) return;
    isLoadingTablesRef.current = true;

    try {
      if (showLoading) setLoading(true);

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/tables`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      const data = result.data || result;
      let tablesArray = Array.isArray(data) ? data : [];

      // ✅ Filter โต๊ะที่เพิ่ง checkout/เคลียร์ออก (ป้องกัน race condition กับ API)
      // Keep filtering until server confirms each table is no longer occupied
      if (closedTableRef.current.size > 0) {
        const codesToFilter = new Set(closedTableRef.current);
        // Remove confirmed-closed tables from the persistent set
        for (const closedCode of codesToFilter) {
          const tableInDb = tablesArray.find(t => t.code === closedCode);
          if (!tableInDb || tableInDb.status !== 'occupied') {
            closedTableRef.current.delete(closedCode); // Won't filter on next load
          }
        }
        // กรองด้วย closedTableRef.current ตัวปัจจุบัน (หลัง delete ด้านบนแล้ว) ไม่ใช่
        // codesToFilter ที่ snapshot ไว้ก่อนหน้า — เดิมใช้ snapshot ทำให้โต๊ะที่เพิ่ง
        // confirm ว่างแล้วใน loop นี้เอง ยังโดนซ่อนไปอีกรอบ ต้องรีเฟรชหน้าถึงจะเห็น
        // (เช่นปุ่ม "เคลียร์โต๊ะ" ที่เรียก loadTables() ต่อทันทีหลัง PUT สำเร็จ)
        tablesArray = tablesArray.filter(t => !closedTableRef.current.has(t.code));
      }

      setTables(tablesArray);

      // ✅ แก้ N+1 Query: ดึง orders ทั้งหมดแค่ครั้งเดียว แล้วจัดกลุ่มตาม tableCode
      const ordersMap = {};
      const occupiedTables = tablesArray.filter(t => t.status === 'occupied' && t.code);

      if (occupiedTables.length > 0) {
        try {
          // ดึง orders ทั้งหมดในคำขอเดียว (ลดจาก N requests เหลือแค่ 1 request)
          const ordersToken = localStorage.getItem('adminToken');
          const allOrdersResponse = await fetch(`${apiUrl}/api/orders`, {
            headers: { 'Authorization': `Bearer ${ordersToken}` }
          });
          const allOrdersResult = await allOrdersResponse.json();
          const allOrders = allOrdersResult.data || allOrdersResult;

          // จัดกลุ่ม orders ตาม tableCode และคำนวณยอดรวม
          if (Array.isArray(allOrders)) {
            allOrders.forEach(order => {
              const tableCode = order.tableCode;
              if (!tableCode) return;

              // คำนวณยอดรวมของ order นี้
              let orderTotal = 0;
              order.items?.forEach(item => {
                if (item.price > 0) {
                  orderTotal += item.price * item.quantity;
                }
              });

              // บวกเข้ายอดรวมของโต๊ะ
              ordersMap[tableCode] = (ordersMap[tableCode] || 0) + orderTotal;
            });
          }
        } catch (err) {
          console.error('Failed to load orders:', err);
          // ถ้าเกิด error ให้ ordersMap เป็น {} (ทุกโต๊ะจะแสดง 0 บาท)
        }
      }

      setTableOrders(ordersMap);
      setError(null);
    } catch (err) {
      console.error('Failed to load tables:', err);
      setError('ไม่สามารถโหลดข้อมูลโต๊ะได้');
    } finally {
      setLoading(false);
      isLoadingTablesRef.current = false; // ✅ ปลดล็อกให้เรียก loadTables() ครั้งต่อไปได้
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveTable();
  };

  // บันทึกโต๊ะ — ใช้ร่วมกันทั้งปุ่ม "บันทึก" ธรรมดา และ "บันทึก & ชำระเงิน"
  // ที่ต้องรอให้บันทึกเสร็จก่อนค่อยพาไปหน้า checkout
  const saveTable = async () => {
    const token = localStorage.getItem('adminToken');
    const url = editingCode
      ? `${apiUrl}/api/tables/${editingCode}`
      : `${apiUrl}/api/tables`;
    const method = editingCode ? 'PUT' : 'POST';

    try {
      // คำนวณราคาบุฟเฟ่ต์
      const adults = formData.adults || 0;
      const children130 = formData.children130 || 0;
      const children120 = formData.children120 || 0;
      const childrenUnder100 = formData.childrenUnder100 || 0;

      const buffetPrice =
        (adults * BUFFET_PRICES.adult) +
        (children130 * BUFFET_PRICES.childBig) +
        (children120 * BUFFET_PRICES.childSmall) +
        (childrenUnder100 * 0);

      // รีฟิลน้ำ — clamp ตามจำนวนคนล่าสุดของฟอร์ม เผื่อพนักงานลดจำนวนคนลงจนน้อยกว่า
      // ที่เคยเลือกรีฟิลไว้ตอนเปิดโต๊ะ
      const totalGuestsForRefill = adults + children130 + children120 + childrenUnder100;
      const clampedRefillQty = Math.max(0, Math.min(totalGuestsForRefill, editRefillQuantity));
      const refillTotal = BUFFET_PRICES.drinkRefill > 0 ? BUFFET_PRICES.drinkRefill * clampedRefillQty : 0;

      // totalPrice = ค่าบุฟเฟ่ต์ + รีฟิลน้ำ (ค่าเมนูสั่งเพิ่มคำนวณแยกจาก orders ที่ checkout)
      // ต้องรวมรีฟิลเข้า totalPrice ตรงนี้เพราะ Checkout.jsx คำนวณยอดสุทธิจาก
      // table.pricing.totalPrice ตรงๆ ไม่ได้บวก drinkRefillTotal แยกอีกที
      const totalPrice = buffetPrice + refillTotal;

      // โต๊ะว่างอยู่แต่กรอกจำนวนลูกค้าแล้วกดบันทึก — เปิดโต๊ะให้อัตโนมัติ (90 นาที)
      const totalGuests = adults + children130 + children120 + childrenUnder100;
      const shouldAutoOpen = formData.status === 'available' && totalGuests > 0;
      let openTimeFields = {};
      if (shouldAutoOpen) {
        const now = new Date();
        const endTime = new Date(now.getTime() + 90 * 60 * 1000); // 90 นาที
        openTimeFields = { startTime: now.toISOString(), endTime: endTime.toISOString(), duration: 90 };
      }

      // เตรียมข้อมูลที่จะส่ง
      const submitData = {
        code: formData.code,
        // ปล่อยว่าง = กลับไปใช้ชื่อดีฟอลต์แบบเดียวกับ TableModel.create ฝั่ง backend
        // เป็นทางล้างชื่อที่เพี้ยนให้กลับมาปกติโดยไม่ต้องพิมพ์เองให้ถูกทุกตัวอักษร
        name: formData.name?.trim() || `โต๊ะ ${formData.code}`,
        seats: formData.seats,
        zone: formData.zone,
        status: shouldAutoOpen ? 'occupied' : formData.status,
        ...openTimeFields,
        guests: {
          adults: adults,
          children130: children130,
          children120: children120,
          childrenUnder100: childrenUnder100
        },
        pricing: {
          adultPrice: BUFFET_PRICES.adult,
          child130Price: BUFFET_PRICES.childBig,
          child120Price: BUFFET_PRICES.childSmall,
          childUnder100Price: 0,
          totalPrice: totalPrice,
          // pricing ถูก $set ทับทั้งก้อนฝั่ง backend (ไม่ merge) — ต้องส่งฟิลด์รีฟิล
          // มาด้วยเสมอตรงนี้ ไม่งั้นค่าที่ลูกค้าเลือกไว้ตอนเปิดโต๊ะจะหายทุกครั้งที่แก้ไขโต๊ะ
          ...(BUFFET_PRICES.drinkRefill > 0 ? {
            drinkRefillPerHead: BUFFET_PRICES.drinkRefill,
            drinkRefillCount: clampedRefillQty,
            drinkRefillTotal: refillTotal,
            // ชื่อรายการต้องส่งมาด้วยเหตุผลเดียวกับสามฟิลด์บน — $set ทับทั้งก้อน ถ้าไม่ส่ง
            // ชื่อที่เก็บไว้ตอนเปิดโต๊ะจะหาย แล้วใบเสร็จพิมพ์ซ้ำจะตกไปใช้ชื่อดีฟอลต์แทน
            drinkRefillLabel: BUFFET_PRICES.drinkRefillLabel || 'รีฟิลน้ำไม่อั้น'
          } : {}),
          // เกณฑ์ส่วนสูง ณ ตอนขาย — หลักเดียวกับราคา/ชื่อรีฟิล เพื่อให้ใบเสร็จพิมพ์ซ้ำได้เกณฑ์ของวันนั้น
          heights: HL.heights
        }
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'เกิดข้อผิดพลาด');
      }

      // อัปเดต orders ที่มีการเปลี่ยนแปลง quantity ของ extras (รวมรายการที่เพิ่งเพิ่มใหม่ด้วย)
      if (editingTableExtras.length > 0) {
        // รวม items ที่มี orderId เดียวกัน — รายการที่เพิ่งเพิ่มใหม่และโต๊ะยังไม่มีออเดอร์เลย
        // จะไม่มี orderId (undefined/null) เก็บแยกไว้ต้องสร้างออเดอร์ใหม่แทนการ PATCH
        const orderUpdates = {};
        const newItemsNoOrder = [];
        // รายการเพิ่มเงินจากหน้าแอดมิน (เช่น เบียร์) ไม่ต้องพิมพ์ใบส่งครัว — พนักงานหยิบเอง
        // ไม่ใช่รายการที่ครัวต้องทำ ต่างจาก POST /orders ปกติที่ลูกค้าสั่งผ่าน QR ซึ่งยังพิมพ์
        // ตามปกติ (ดู order.route.js: route นั้น skip พิมพ์เฉพาะตอน req.admin เป็นคนสร้างเอง)
        editingTableExtras.forEach(extra => {
          const item = {
            menuItemId: extra.menuItemId,
            name: extra.name,
            price: extra.price,
            quantity: extra.quantity,
            categoryId: extra.categoryId ?? ''
          };
          if (extra.orderId) {
            if (!orderUpdates[extra.orderId]) orderUpdates[extra.orderId] = [];
            orderUpdates[extra.orderId].push(item);
          } else {
            newItemsNoOrder.push(item);
          }
        });

        // อัปเดตแต่ละ order ที่มีอยู่แล้ว (เพิ่ม item ไม่ว่า quantity จะเป็นเท่าไหร่ API จะ filter เอง)
        // — ไม่พิมพ์ใบส่งครัว (ดูคอมเมนต์ด้านบน)
        for (const [orderId, items] of Object.entries(orderUpdates)) {
          try {
            await fetch(`${apiUrl}/api/orders/${orderId}/items`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ items })
            });
          } catch (err) {
            console.error(`Failed to update order ${orderId}:`, err);
          }
        }

        // โต๊ะที่ยังไม่เคยมีออเดอร์เลย (ไม่มี orderId ให้ผูก) — สร้างออเดอร์ใหม่ให้
        if (newItemsNoOrder.length > 0) {
          try {
            await fetch(`${apiUrl}/api/orders`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                tableCode: formData.code,
                items: newItemsNoOrder,
                status: 'submitted',
                // กันเหนียวไว้อีกชั้น เผื่อ X-Branch-Id header หลุดไปด้วยเหตุผลอื่นในอนาคต —
                // requireBranchContext เชื่อ branchId ใน body ได้เหมือน header (ดู branch.js)
                branchId: effectiveBranchId
              })
            });
          } catch (err) {
            console.error('Failed to create order for new extras:', err);
          }
        }
      }

      // Reset form
      setFormData({
        code: '',
        name: '',
        seats: 4,
        adults: 0,
        children130: 0,
        children120: 0,
        childrenUnder100: 0,
        zone: 'main',
        status: 'available'
      });
      setEditingCode(null);
      setShowEditModal(false); // ปิด modal

      // Reload list
      await loadTables();
      return true;
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      return false;
    }
  };

  // บันทึกโต๊ะแล้วไปหน้า checkout ต่อเลย — เผื่อพนักงานแก้จำนวนคน/รายการเพิ่มเงิน
  // ก่อนรับเงินแล้วไม่อยากปิด modal มากดชำระเงินอีกรอบ
  const handleSaveAndCheckout = async () => {
    const code = editingCode;
    const ok = await saveTable();
    if (ok) navigate('/checkout', { state: { selectedTableCode: code } });
  };

  const handleEdit = async (table) => {
    setEditingCode(table.code);

    setFormData({
      code: table.code,
      name: table.name,
      seats: table.seats,
      adults: table.guests?.adults || 0,
      children130: table.guests?.children130 || 0,
      children120: table.guests?.children120 || 0,
      childrenUnder100: table.guests?.childrenUnder100 || 0,
      zone: table.zone || 'main',
      status: table.status || 'available'
    });

    setEditRefillQuantity(table.pricing?.drinkRefillCount || 0);

    // ดึงรายการเมนูเพิ่มเงินของโต๊ะนี้
    if (table.status === 'occupied') {
      try {
        const ordersResponse = await fetch(`${apiUrl}/api/orders?tableCode=${table.code}`);
        const ordersResult = await ordersResponse.json();
        const orders = ordersResult.data || ordersResult;

        // เก็บ items แต่ละตัวแยกกัน (ไม่รวม) เพื่อให้ update กลับได้ถูกต้อง
        const extrasWithOrderInfo = [];
        orders.forEach(order => {
          order.items?.forEach((item, itemIndex) => {
            if (item.price > 0) {
              extrasWithOrderInfo.push({
                menuItemId: item.menuItemId,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                originalQuantity: item.quantity, // เก็บค่าเดิมไว้เปรียบเทียบ
                orderId: order.id,
                itemIndex: itemIndex
              });
            }
          });
        });
        setEditingTableExtras(extrasWithOrderInfo);
      } catch (err) {
        console.error('Failed to load extras:', err);
        setEditingTableExtras([]);
      }
    } else {
      setEditingTableExtras([]);
    }

    setAddExtraMenuItemId('');
    loadExtraMenuOptions();

    // ดึงประวัติเปิดหน้า QR/เว็บของโต๊ะนี้ — ช่วยตรวจสอบย้อนหลังตอนโต๊ะมีออเดอร์แปลกๆ
    setScanLimit(20);
    await loadTableScans(table.code, 20);
    setShowScanHistory(false);

    // เปิด modal
    setShowEditModal(true);
  };

  // ดึงประวัติเปิดหน้าของโต๊ะ แยกออกมาเพราะต้องเรียกซ้ำตอนกด "ดูเพิ่ม" ด้วยจำนวนที่มากขึ้น
  const loadTableScans = async (tableCode, limit) => {
    setLoadingScans(true);
    try {
      const token = localStorage.getItem('adminToken');
      const scansResponse = await fetch(`${apiUrl}/api/tables/${tableCode}/scans?limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const scansResult = await scansResponse.json();
      setEditingTableScans(scansResult.data || []);
    } catch (err) {
      console.error('Failed to load scan log:', err);
      setEditingTableScans([]);
    } finally {
      setLoadingScans(false);
    }
  };

  /**
   * พิมพ์ใบเสร็จของโต๊ะที่ยังไม่ได้ชำระเงิน ให้ลูกค้าตรวจยอดก่อนจ่าย
   *
   * ใช้ /printer/print-checkout ตัวเดียวกับตอนเช็คบิลจริง แต่ตั้งใจไม่ส่ง paymentMethod
   * และ payments ไปด้วย — receipt-template.js พิมพ์บรรทัด "ชำระโดย:" ต่อเมื่อมีค่าพวกนี้
   * ใบที่ออกมาจึงเป็นใบแจ้งยอด ไม่ใช่ใบที่อ้างว่าจ่ายเงินแล้วทั้งที่ยังไม่ได้จ่าย
   *
   * ต้องดึง orders มาเองตรงนี้ เพราะ tableOrders ที่หน้านี้เก็บไว้เป็นแค่ยอดรวมเป็นตัวเลข
   * ไม่ใช่รายการ ส่วนใบเสร็จต้องพิมพ์แยกทีละรายการ
   */
  const handlePrintBill = async (table) => {
    setPrintingTable(table.code);
    try {
      const token = localStorage.getItem('adminToken');

      const ordersResponse = await fetch(`${apiUrl}/api/orders?tableCode=${table.code}`);
      const ordersResult = await ordersResponse.json();
      const orders = ordersResult.data || ordersResult || [];

      // คิดยอดด้วยสูตรเดียวกับ Checkout.jsx (calculateMenuTotal / calculateGrandTotal)
      const menuTotal = orders.reduce((sum, order) => (
        sum + (order.items || []).reduce((s, item) => (
          s + (Number(item.price) || 0) * (Number(item.quantity) || 0)
        ), 0)
      ), 0);
      // ค่าบุฟเฟ่ต์: pricing.totalPrice มาก่อน buffet.total เสมอ
      // pricing ถูกล้างทุกทางที่ปิดโต๊ะ (checkout / เคลียร์โต๊ะ / ยกเลิกโต๊ะ) ถ้ามีค่าอยู่แปลว่า
      // เป็นของรอบนี้แน่นอน ส่วน buffet ไม่ถูกล้างตอน "เคลียร์โต๊ะ" จึงค้างข้ามรอบได้
      // ถ้าเอา buffet มาก่อน จะคิดเงินด้วยยอดของลูกค้ารอบก่อน และขาดค่ารีฟิลน้ำ (buffet.total ไม่รวมรีฟิล)
      const buffetTotal = table.pricing?.totalPrice ?? table.buffet?.total ?? 0;

      const response = await fetch(`${apiUrl}/printer/print-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Branch-Id': effectiveBranchId
        },
        body: JSON.stringify({
          table,
          orders,
          buffetTotal,
          menuTotal,
          grandTotal: buffetTotal + menuTotal
        })
      });

      const result = await response.json();
      if (!response.ok) {
        setMessage({ type: 'error', text: result.message || 'พิมพ์ใบเสร็จไม่สำเร็จ' });
        return;
      }
      setMessage({ type: 'success', text: `ส่งใบเสร็จโต๊ะ ${table.code} ไปที่เครื่องพิมพ์แล้ว` });
    } catch (err) {
      console.error('Print bill failed:', err);
      setMessage({ type: 'error', text: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้' });
    } finally {
      setPrintingTable(null);
    }
  };

  // เพิ่มรายการเมนูใหม่ (เช่น เบียร์, รีฟิลน้ำ) ให้โต๊ะที่กำลังแก้ไข — ถ้ามีรายการนี้อยู่แล้ว
  // แค่บวก quantity เพิ่ม ไม่สร้างแถวซ้ำ ผูก orderId ของออเดอร์ที่มีอยู่แล้ว (ถ้ามี) เพื่อรวม
  // เข้าออเดอร์เดียวกันตอนบันทึก ถ้าโต๊ะยังไม่มีออเดอร์เลยจะปล่อย orderId ว่างไว้ — handleSubmit
  // จะสร้างออเดอร์ใหม่ให้แทนตอนบันทึก
  const handleAddExtraItem = () => {
    if (!addExtraMenuItemId) return;
    const menuItem = extraMenuOptions.find(m => m.id === addExtraMenuItemId);
    if (!menuItem) return;

    setEditingTableExtras(prev => {
      const existingIndex = prev.findIndex(e => e.menuItemId === menuItem.id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = { ...next[existingIndex], quantity: next[existingIndex].quantity + 1 };
        return next;
      }
      const existingOrderId = prev.find(e => e.orderId)?.orderId ?? null;
      return [
        ...prev,
        {
          menuItemId: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          quantity: 1,
          categoryId: menuItem.categoryId ?? '',
          orderId: existingOrderId
        }
      ];
    });
    setAddExtraMenuItemId('');
  };

  // ลบรายการทิ้งทั้งหมด (ลูกค้าเปลี่ยนใจ ไม่เอาแล้ว) — ต่างจากปุ่ม − ที่ต้องกดลดทีละ 1
  // รายการที่เพิ่งเพิ่มใหม่ในรอบแก้ไขนี้ (ยังไม่มี orderId เพราะยังไม่เคยบันทึก) ตัดออกจาก
  // state ไปเลยได้ปลอดภัย — แต่รายการเดิมที่มีอยู่แล้วในออเดอร์ (มี orderId) ต้องคง entry ไว้
  // แล้วตั้ง quantity เป็น 0 แทนการตัดทิ้ง เพราะ PATCH /api/orders/:id/items merge จากรายการ
  // ที่ "ส่งไป" เท่านั้น — ถ้าตัดออกจาก payload เฉยๆ ฝั่ง backend จะเข้าใจว่าไม่ได้แก้ ไม่ใช่ลบ
  const handleRemoveExtraItem = (index) => {
    setEditingTableExtras(prev => {
      const target = prev[index];
      if (!target.orderId) {
        return prev.filter((_, i) => i !== index);
      }
      const next = [...prev];
      next[index] = { ...next[index], quantity: 0 };
      return next;
    });
  };

  const handleDelete = async (tableCodeOrId) => {
    if (!await confirm('คุณต้องการลบโต๊ะนี้หรือไม่?', { title: 'ลบโต๊ะ', confirmText: 'ลบ', danger: true })) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/api/tables/${tableCodeOrId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'ไม่สามารถลบโต๊ะได้');
      }

      await loadTables();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleCancel = () => {
    setEditingCode(null);
    setShowEditModal(false);
    setEditingTableExtras([]);
    setEditRefillQuantity(0);
    setFormData({
      code: '',
      name: '',
      seats: 4,
      adults: 0,
      children130: 0,
      children120: 0,
      childrenUnder100: 0,
      zone: 'main',
      status: 'available'
    });
  };

  const handleViewQR = (code) => {
    // window.open ยิง navigation ตรง ๆ แนบ Authorization header ไม่ได้ เลยไม่มีทางผ่าน
    // requireBranchContext ด้วย branchId (ต้อง req.admin?.isOwner ซึ่งไม่มีทางเซ็ตได้จาก
    // navigation เปล่า ๆ) ต้องส่ง branch slug แทน — เป็น path สาธารณะที่ไม่ต้องล็อกอิน
    // อยู่แล้ว (เหมือนที่ลูกค้าใช้ ?branch=slug ตรง ๆ) และไม่พึ่ง fallback สาขาเดียวที่
    // active ซึ่งพังทันทีที่มีสาขาที่ 2 เปิดพร้อมกัน
    const qrUrl = `${apiUrl}/api/tables/${code}/qr?branch=${encodeURIComponent(currentBranch?.slug ?? '')}`;
    window.open(qrUrl, '_blank');
  };

  const handleClearTable = async (tableCode) => {
    if (!await confirm('จะรีเซ็ตสถานะโต๊ะ ลบข้อมูลลูกค้า และลบออเดอร์จากหน้าจอครัว', { title: `เคลียร์โต๊ะ ${tableCode}?`, confirmText: 'เคลียร์โต๊ะ', danger: true })) {
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      // 1. ลบออเดอร์ของโต๊ะนี้ออกจากระบบ (และหน้าจอครัว)
      const deleteOrdersResponse = await fetch(`${apiUrl}/api/orders/table/${tableCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!deleteOrdersResponse.ok) {
        console.warn('ไม่สามารถลบออเดอร์ได้ แต่จะดำเนินการเคลียร์โต๊ะต่อ');
      }

      // 2. รีเซ็ตสถานะโต๊ะ
      const response = await fetch(`${apiUrl}/api/tables/${tableCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'available',
          guests: null,
          pricing: null,
          startTime: null,
          endTime: null,
          duration: null,
          // ต้องส่ง buffet มาด้วย — ฝั่ง backend ล้าง buffet เฉพาะตอนส่งค่ามาเท่านั้น
          // (guests: null ล้างให้แค่ guests/pricing/startTime/endTime/duration)
          // ไม่ส่ง → buffet ของลูกค้าคนก่อนค้างข้ามไปถึงลูกค้าคนถัดไป
          buffet: null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'ไม่สามารถเคลียร์โต๊ะได้');
      }

      setMessage({ type: 'success', text: `เคลียร์โต๊ะ ${tableCode} สำเร็จ (ออเดอร์ถูกลบออกจากหน้าจอครัวแล้ว)` });
      await loadTables();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleOpenTable = (table) => {
    setSelectedTable(table);
    setBuffetData({ adults: 0, childrenBig: 0, childrenSmall: 0 });
    setShowBuffetModal(true);
  };

  const calculateBuffetTotal = () => {
    return (
      (buffetData.adults * BUFFET_PRICES.adult) +
      (buffetData.childrenBig * BUFFET_PRICES.childBig) +
      (buffetData.childrenSmall * BUFFET_PRICES.childSmall)
    );
  };

  const confirmOpenTable = async () => {
    const total = calculateBuffetTotal();

    if (total === 0) {
      setMessage({ type: 'error', text: 'กรุณาระบุจำนวนคนอย่างน้อย 1 คน' });
      return;
    }

    const token = localStorage.getItem('adminToken');

    try {
      const response = await fetch(`${apiUrl}/api/tables/${selectedTable.code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'occupied',
          buffet: {
            adults: buffetData.adults,
            childrenBig: buffetData.childrenBig,
            childrenSmall: buffetData.childrenSmall,
            total: total,
            startTime: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error('ไม่สามารถเปิดโต๊ะได้');
      }

      const openedCode = selectedTable.code;
      setShowBuffetModal(false);
      setSelectedTable(null);
      setBuffetData({ adults: 0, childrenBig: 0, childrenSmall: 0 });
      await loadTables();
      setMessage({ type: 'success', text: `เปิดโต๊ะ ${openedCode} สำเร็จ ยอดบุฟเฟ่ต์: ${total.toFixed(2)} บาท` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — จัดการโต๊ะต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการแก้ไขผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">กำลังโหลดข้อมูล...</div>;
  }

  return (
    <div className="table-management">
      <Toast message={message} onDismiss={() => setMessage(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>จัดการโต๊ะ</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        background: 'var(--surface)',
        padding: '15px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
      }}>
        <button
          onClick={() => setStatusFilter('all')}
          style={{
            padding: '8px 16px',
            border: statusFilter === 'all' ? 'none' : '1px solid var(--border-strong)',
            borderRadius: '8px',
            background: statusFilter === 'all' ? 'var(--accent)' : 'var(--surface-2)',
            color: statusFilter === 'all' ? '#1a1206' : 'var(--muted)',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          📋 ทั้งหมด ({tables.length})
        </button>
        <button
          onClick={() => setStatusFilter('occupied')}
          style={{
            padding: '8px 16px',
            border: statusFilter === 'occupied' ? 'none' : '1px solid var(--border-strong)',
            borderRadius: '8px',
            background: statusFilter === 'occupied' ? 'var(--accent)' : 'var(--surface-2)',
            color: statusFilter === 'occupied' ? '#1a1206' : 'var(--muted)',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          🍽️ มีลูกค้า ({tables.filter(t => t.status === 'occupied').length})
        </button>
        <button
          onClick={() => setStatusFilter('available')}
          style={{
            padding: '8px 16px',
            border: statusFilter === 'available' ? 'none' : '1px solid var(--border-strong)',
            borderRadius: '8px',
            background: statusFilter === 'available' ? 'var(--ok)' : 'var(--surface-2)',
            color: statusFilter === 'available' ? '#1a1206' : 'var(--muted)',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          ✅ ว่าง ({tables.filter(t => t.status === 'available').length})
        </button>
        <button
          onClick={() => setStatusFilter('nearExpiry')}
          style={{
            padding: '8px 16px',
            border: statusFilter === 'nearExpiry' ? 'none' : '1px solid var(--border-strong)',
            borderRadius: '8px',
            background: statusFilter === 'nearExpiry' ? 'var(--warn)' : 'var(--surface-2)',
            color: statusFilter === 'nearExpiry' ? '#1a1206' : 'var(--muted)',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          ⏰ ใกล้หมดเวลา ({nearExpiryCount})
        </button>
      </div>

      {/* Modal สำหรับแก้ไขโต๊ะ - Compact Design */}
      <Modal open={showEditModal} onClose={() => { setShowEditModal(false); setEditingCode(null); setEditingTableExtras([]); setEditRefillQuantity(0); }} maxWidth="420px">
            {/* Header */}
            <div style={{
              margin: '-2rem -2rem 1rem -2rem',
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-strong)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--surface-2)',
              borderRadius: '16px 16px 0 0'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text)', fontWeight: '600' }}>
                แก้ไขโต๊ะ {editingCode}
              </h3>
              <button
                onClick={() => { setShowEditModal(false); setEditingCode(null); setEditingTableExtras([]); }}
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)', padding: '4px' }}
              >✕</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '16px 20px' }}>
              {/* Section: ข้อมูลโต๊ะ */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>รหัสโต๊ะ</label>
                  <input
                    className="dark-input"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '0.95rem', backgroundColor: 'var(--surface-2)', color: 'var(--text)', boxSizing: 'border-box' }}
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '4px' }}>ที่นั่ง</label>
                  <input
                    className="dark-input"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '0.95rem', backgroundColor: 'var(--surface-2)', color: 'var(--text)', boxSizing: 'border-box' }}
                    type="number"
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) || 4 })}
                    min="1" max="20"
                  />
                </div>
              </div>

              {/* Section: จำนวนลูกค้า */}
              <div style={{ background: 'var(--surface-2)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text)', fontWeight: '600', marginBottom: '10px' }}>
                  ลูกค้า: {(formData.adults || 0) + (formData.children130 || 0) + (formData.children120 || 0) + (formData.childrenUnder100 || 0)} คน
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 70px), 1fr))', gap: '8px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <label style={{ fontSize: '0.75rem', color: 'var(--muted)', display: 'block' }}>ผู้ใหญ่</label>
                    <input
                      className="dark-input"
                      style={{ width: '100%', padding: '6px', border: '1px solid var(--border-strong)', borderRadius: '4px', fontSize: '0.9rem', textAlign: 'center', backgroundColor: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }}
                      type="number" value={formData.adults} onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value) || 0 })} min="0" max="20"
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <label style={{ fontSize: '0.75rem', color: 'var(--muted)', display: 'block' }}>130+</label>
                    <input
                      className="dark-input"
                      style={{ width: '100%', padding: '6px', border: '1px solid var(--border-strong)', borderRadius: '4px', fontSize: '0.9rem', textAlign: 'center', backgroundColor: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }}
                      type="number" value={formData.children130} onChange={(e) => setFormData({ ...formData, children130: parseInt(e.target.value) || 0 })} min="0" max="20"
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <label style={{ fontSize: '0.75rem', color: 'var(--muted)', display: 'block' }}>100-120</label>
                    <input
                      className="dark-input"
                      style={{ width: '100%', padding: '6px', border: '1px solid var(--border-strong)', borderRadius: '4px', fontSize: '0.9rem', textAlign: 'center', backgroundColor: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }}
                      type="number" value={formData.children120} onChange={(e) => setFormData({ ...formData, children120: parseInt(e.target.value) || 0 })} min="0" max="20"
                    />
                  </div>
                </div>
              </div>

              {/* Section: รีฟิลน้ำ — ลูกค้าเลือกไว้ตอนเปิดโต๊ะ ปรับได้ถ้าเปลี่ยนใจระหว่างมื้อ */}
              {BUFFET_PRICES.drinkRefill > 0 && (() => {
                const totalGuestsNow = (formData.adults || 0) + (formData.children130 || 0) + (formData.children120 || 0) + (formData.childrenUnder100 || 0);
                const clampedQty = Math.max(0, Math.min(totalGuestsNow, editRefillQuantity));
                return (
                  <div style={{ background: 'var(--surface-2)', borderRadius: '8px', padding: '12px', marginBottom: '16px', border: '1px solid var(--border-strong)' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text)', fontWeight: '600', marginBottom: '10px' }}>
                      🥤 {BUFFET_PRICES.drinkRefillLabel} ({BUFFET_PRICES.drinkRefill}฿/คน)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => setEditRefillQuantity(q => Math.max(0, q - 1))}
                        disabled={clampedQty <= 0}
                        style={{
                          width: 36, height: 36, borderRadius: '50%', border: 'none',
                          background: clampedQty <= 0 ? 'var(--border-strong)' : 'var(--danger)', color: clampedQty <= 0 ? 'var(--muted)' : '#1a1206',
                          fontSize: '1.1rem', fontWeight: 700, cursor: clampedQty <= 0 ? 'not-allowed' : 'pointer'
                        }}
                      >−</button>
                      <div style={{ minWidth: 60, textAlign: 'center' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text)' }}>{clampedQty}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>จาก {totalGuestsNow} คน</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditRefillQuantity(q => Math.min(totalGuestsNow, q + 1))}
                        disabled={clampedQty >= totalGuestsNow}
                        style={{
                          width: 36, height: 36, borderRadius: '50%', border: 'none',
                          background: clampedQty >= totalGuestsNow ? 'var(--border-strong)' : 'var(--ok)', color: clampedQty >= totalGuestsNow ? 'var(--muted)' : '#1a1206',
                          fontSize: '1.1rem', fontWeight: 700, cursor: clampedQty >= totalGuestsNow ? 'not-allowed' : 'pointer'
                        }}
                      >+</button>
                    </div>
                    {clampedQty > 0 && (
                      <div style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--muted)', marginTop: '8px' }}>
                        รวม {(BUFFET_PRICES.drinkRefill * clampedQty).toLocaleString()} บาท
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Section: รายการเมนูเพิ่มเงิน — อยู่นอกบุฟเฟ่ต์ เช่น เบียร์, รีฟิลน้ำ */}
              <div style={{ background: '#1d1a12', borderRadius: '8px', padding: '12px', marginBottom: '16px', border: '1px solid #4a3c1c' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--warn)', fontWeight: '600', marginBottom: '10px' }}>
                  🍺 รายการเพิ่มเงิน
                </div>
                {editingTableExtras.some(item => item.quantity > 0) && (
                  <>
                  {editingTableExtras.map((item, index) => item.quantity > 0 && (
                    <div key={index} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      padding: '8px',
                      background: 'var(--surface-2)',
                      borderRadius: '6px',
                      marginBottom: '6px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: 'var(--text)' }}>{item.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{item.price} บาท/ชิ้น</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const newExtras = [...editingTableExtras];
                              if (newExtras[index].quantity > 0) {
                                newExtras[index].quantity -= 1;
                              }
                              setEditingTableExtras(newExtras);
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              border: 'none',
                              background: 'var(--danger)',
                              color: '#1a1206',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >−</button>
                          <span style={{
                            minWidth: '30px',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '1rem'
                          }}>{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const newExtras = [...editingTableExtras];
                              newExtras[index].quantity += 1;
                              setEditingTableExtras(newExtras);
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              border: 'none',
                              background: 'var(--ok)',
                              color: '#1a1206',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >+</button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            minWidth: '70px',
                            textAlign: 'right',
                            fontWeight: '600',
                            color: 'var(--danger)'
                          }}>
                            {(item.price * item.quantity).toLocaleString()} ฿
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveExtraItem(index)}
                            title="ลบรายการนี้ทิ้งทั้งหมด"
                            style={{
                              padding: '6px 8px',
                              borderRadius: '6px',
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--muted)',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >🗑️</button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #4a3c1c',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: '700',
                    color: 'var(--warn)'
                  }}>
                    <span>รวมเพิ่มเงิน:</span>
                    <span>{editingTableExtras.reduce((sum, item) => sum + (item.price * item.quantity), 0).toLocaleString()} บาท</span>
                  </div>
                  </>
                )}

                {/* เพิ่มรายการใหม่ — เลือกจากเมนูที่มีราคา (นอกเหนือบุฟเฟ่ต์) */}
                <div style={{ display: 'flex', gap: '8px', marginTop: editingTableExtras.length > 0 ? '10px' : 0 }}>
                  <select
                    className="dark-input"
                    value={addExtraMenuItemId}
                    onChange={(e) => setAddExtraMenuItemId(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: '6px',
                      border: '1px solid #4a3c1c',
                      background: 'var(--surface-2)',
                      color: 'var(--text)',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">
                      {extraMenuOptions.length === 0 ? '(ไม่มีเมนูนอกบุฟเฟ่ต์)' : '-- เลือกรายการที่จะเพิ่ม --'}
                    </option>
                    {extraMenuOptions.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.price}฿)</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddExtraItem}
                    disabled={!addExtraMenuItemId}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: addExtraMenuItemId ? 'var(--warn)' : '#4a3c1c',
                      color: 'var(--surface)',
                      fontWeight: '700',
                      cursor: addExtraMenuItemId ? 'pointer' : 'not-allowed'
                    }}
                  >
                    + เพิ่ม
                  </button>
                </div>
              </div>

              {/* ปุ่มหลัก */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ flex: 1, padding: '10px', background: 'var(--accent)', color: 'var(--text)', border: 'none', borderRadius: '6px', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer' }}>
                  บันทึก
                </button>
                <button type="button" onClick={handleCancel} style={{ padding: '10px 16px', background: 'var(--border-strong)', color: 'var(--muted)', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '0.95rem', cursor: 'pointer' }}>
                  ยกเลิก
                </button>
              </div>

              {/* บันทึก & ชำระเงิน — แสดงเฉพาะโต๊ะที่มีลูกค้า ให้แก้ไขเสร็จแล้วกดชำระได้เลยไม่ต้องปิด modal ไปกดที่การ์ดโต๊ะอีกที */}
              {editingCode && tables.find(t => t.code === editingCode)?.status === 'occupied' && (
                <button
                  type="button"
                  onClick={handleSaveAndCheckout}
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    padding: '10px',
                    background: 'linear-gradient(135deg, var(--ok) 0%, var(--ok) 100%)',
                    color: 'var(--text)',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  💰 บันทึก & ชำระเงิน
                </button>
              )}

              {/* ย้ายโต๊ะ & ยกเลิกโต๊ะ - แสดงเฉพาะโต๊ะที่มีลูกค้า */}
              {editingCode && tables.find(t => t.code === editingCode)?.status === 'occupied' && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-strong)' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '6px' }}>
                      🔄 ย้ายโต๊ะ
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        className="dark-input"
                        id="moveTableSelect"
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '0.9rem', backgroundColor: 'var(--surface-2)', color: 'var(--text)' }}
                        defaultValue=""
                      >
                        <option value="" disabled>เลือกโต๊ะว่าง...</option>
                        {tables.filter(t => t.status === 'available' && t.code !== editingCode).map(t => (
                          <option key={t.code} value={t.code}>โต๊ะ {t.code} ({t.name})</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={async () => {
                          const selectElement = document.getElementById('moveTableSelect');
                          const targetTableCode = selectElement.value;

                          if (!targetTableCode) {
                            setMessage({ type: 'error', text: 'กรุณาเลือกโต๊ะปลายทาง' });
                            return;
                          }

                          if (!await confirm(`ย้ายลูกค้าจากโต๊ะ ${editingCode} ไปโต๊ะ ${targetTableCode}`, { title: 'ย้ายโต๊ะ?', confirmText: 'ย้าย' })) {
                            return;
                          }

                          const token = localStorage.getItem('adminToken');
                          const sourceTable = tables.find(t => t.code === editingCode);

                          try {
                            console.log('Starting table move:', editingCode, '→', targetTableCode);

                            // 1. ย้ายข้อมูลลูกค้าไปโต๊ะใหม่
                            console.log('Step 1: Moving customer data to target table');
                            const moveResponse = await fetch(`${apiUrl}/api/tables/${targetTableCode}`, {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({
                                status: 'occupied',
                                guests: sourceTable.guests,
                                pricing: sourceTable.pricing,
                                startTime: sourceTable.startTime,
                                endTime: sourceTable.endTime,
                                duration: sourceTable.duration
                              })
                            });

                            if (!moveResponse.ok) {
                              const errorData = await moveResponse.json();
                              console.error('Move response failed:', errorData);
                              throw new Error('ไม่สามารถย้ายข้อมูลไปโต๊ะใหม่ได้: ' + (errorData.message || ''));
                            }
                            console.log('Step 1 complete');

                            // 2. ย้ายออเดอร์ทั้งหมดไปโต๊ะใหม่
                            console.log('Step 2: Moving orders');
                            const ordersResponse = await fetch(`${apiUrl}/api/orders?tableCode=${editingCode}`);
                            const ordersResult = await ordersResponse.json();
                            const orders = ordersResult.data || ordersResult;
                            console.log('Found orders:', orders.length);

                            for (const order of orders) {
                              console.log('Moving order:', order.id);
                              const updateResponse = await fetch(`${apiUrl}/api/orders/${order.id}`, {
                                method: 'PATCH',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ tableCode: targetTableCode })
                              });

                              if (!updateResponse.ok) {
                                const errorData = await updateResponse.json();
                                console.error('Order update failed:', order.id, errorData);
                                throw new Error(`ไม่สามารถย้าย order ${order.id}: ${errorData.message || ''}`);
                              }
                            }
                            console.log('Step 2 complete');

                            // 3. รีเซ็ตโต๊ะเดิม
                            console.log('Step 3: Resetting source table');
                            const resetResponse = await fetch(`${apiUrl}/api/tables/${editingCode}`, {
                              method: 'PUT',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({
                                status: 'available',
                                guests: null,
                                pricing: null,
                                startTime: null,
                                endTime: null,
                                duration: null,
                                // โต๊ะปลายทางรับ buffet ไปแล้ว — ต้องล้างของโต๊ะต้นทางด้วย
                                // ไม่งั้นค้างไว้ไปโผล่กับลูกค้าคนถัดไปที่นั่งโต๊ะนี้
                                buffet: null
                              })
                            });

                            if (!resetResponse.ok) {
                              const errorData = await resetResponse.json();
                              console.error('Reset failed:', errorData);
                              throw new Error('ไม่สามารถรีเซ็ตโต๊ะเดิมได้: ' + (errorData.message || ''));
                            }
                            console.log('Step 3 complete');

                            setMessage({ type: 'success', text: `ย้ายโต๊ะสำเร็จ! จาก: ${editingCode} → ไป: ${targetTableCode} (ย้ายออเดอร์ ${orders.length} รายการ)` });
                            setShowEditModal(false);
                            setEditingCode(null);
                            await loadTables();
                          } catch (err) {
                            console.error('Table move error:', err);
                            setMessage({ type: 'error', text: 'เกิดข้อผิดพลาด: ' + err.message });
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: 'var(--info)',
                          color: '#1a1206',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        ย้าย
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (await confirm('จะรีเซ็ตสถานะเป็นว่างและลบข้อมูลลูกค้า', { title: `ยกเลิกโต๊ะ ${editingCode}?`, confirmText: 'ยกเลิกโต๊ะ', danger: true })) {
                        const token = localStorage.getItem('adminToken');
                        try {
                          const response = await fetch(`${apiUrl}/api/tables/${editingCode}`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              status: 'available',
                              guests: null,
                              pricing: null,
                              startTime: null,
                              endTime: null,
                              duration: null,
                              buffet: null
                            })
                          });
                          if (response.ok) {
                            setMessage({ type: 'success', text: `ยกเลิกโต๊ะ ${editingCode} สำเร็จ` });
                            setShowEditModal(false);
                            setEditingCode(null);
                            await loadTables();
                          } else {
                            throw new Error('ไม่สามารถยกเลิกโต๊ะได้');
                          }
                        } catch (err) {
                          setMessage({ type: 'error', text: err.message });
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'var(--warn)',
                      color: '#1a1206',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    🚫 ยกเลิกโต๊ะ
                  </button>
                </div>
              )}

              {/* Section: ประวัติเปิดหน้า — แยกว่ามาจาก QR จริง หรือเปิดผ่านทางอื่น ช่วยตรวจสอบ
                  ย้อนหลังตอนโต๊ะมีออเดอร์แปลกๆ ทั้งที่หน้าร้านไม่มีลูกค้า
                  พับเก็บไว้ก่อนเพราะไม่ค่อยได้ใช้ ไม่ให้บังข้อมูลที่ใช้บ่อยกว่า */}
              {editingTableScans.length > 0 && (
                <div style={{ background: 'var(--surface-2)', borderRadius: '8px', marginTop: '16px', border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setShowScanHistory(v => !v)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: 'var(--text)',
                      fontWeight: '600'
                    }}
                  >
                    <span>📱 ประวัติเปิดหน้าล่าสุด ({editingTableScans.length})</span>
                    <span>{showScanHistory ? '▲' : '▼'}</span>
                  </button>
                  {showScanHistory && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflowY: 'auto', padding: '0 12px 12px' }}>
                      {editingTableScans.map((scan, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--muted)' }}>
                          <span>{scan.source === 'qr' ? '✅ สแกน QR' : '🔗 เปิดผ่านลิงก์อื่น'}</span>
                          <span>{new Date(scan.createdAt).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}

                      {/* รายการเต็มเพดานที่ขอมา = อาจมีเก่ากว่านี้อีก ให้กดขยายได้ถึง 200
                          (เพดานฝั่ง API) ไม่งั้นไล่ย้อนหาเหตุการณ์เมื่อหลายวันก่อนไม่ได้เลย */}
                      {editingTableScans.length >= scanLimit && scanLimit < 200 && (
                        <button
                          type="button"
                          disabled={loadingScans}
                          onClick={() => {
                            const next = Math.min(scanLimit + 50, 200);
                            setScanLimit(next);
                            loadTableScans(editingCode, next);
                          }}
                          style={{
                            marginTop: '6px',
                            padding: '6px',
                            background: 'transparent',
                            border: '1px solid var(--border-strong)',
                            borderRadius: '6px',
                            color: 'var(--info)',
                            fontSize: '0.75rem',
                            cursor: loadingScans ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {loadingScans ? 'กำลังโหลด...' : '↓ ดูย้อนหลังเพิ่ม'}
                        </button>
                      )}

                      {/* กันอ่านผิด: QR ที่พิมพ์ก่อน 3 ก.ย. 2569 ไม่มี ?src=qr ฝังอยู่ (เพิ่มพร้อม
                          ระบบ log นี้) สแกนจากสติกเกอร์เก่าจึงขึ้นว่า "เปิดผ่านลิงก์อื่น" ทั้งที่
                          เป็นการสแกนจริง — สองแบบนี้จะแยกกันได้จริงต่อเมื่อพิมพ์ QR ชุดใหม่แล้ว */}
                      {!editingTableScans.some(scan => scan.source === 'qr') && editingTableScans.length > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '0.7rem', color: 'var(--faint)', lineHeight: 1.5 }}>
                          ℹ️ ถ้า QR บนโต๊ะพิมพ์ไว้ก่อน 3 ก.ย. 2569 การสแกนจริงจะขึ้นว่า "เปิดผ่านลิงก์อื่น"
                          เพราะ QR ชุดเก่าไม่มีตัวระบุฝังอยู่ — พิมพ์ QR ใหม่แล้วถึงจะแยกสองแบบนี้ได้
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </form>
      </Modal>

      <div className="tables-list">
        <h3>
          {statusFilter === 'all' && `รายการโต๊ะทั้งหมด (${tables.length})`}
          {statusFilter === 'occupied' && `โต๊ะที่มีลูกค้า (${tables.filter(t => t.status === 'occupied').length})`}
          {statusFilter === 'available' && `โต๊ะว่าง (${tables.filter(t => t.status === 'available').length})`}
          {statusFilter === 'nearExpiry' && `โต๊ะใกล้หมดเวลา (${nearExpiryCount})`}
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
          gap: '20px',
          marginTop: '20px'
        }}>
          {tables
            .filter(table => {
              if (statusFilter === 'all') return true;
              if (statusFilter === 'occupied') return table.status === 'occupied';
              if (statusFilter === 'available') return table.status === 'available';
              if (statusFilter === 'nearExpiry') return isNearExpiry(table);
              return true;
            })
            .map((table) => (
            <div
              key={table.code}
              onClick={() => handleEdit(table)}
              style={{
                background: table.status === 'occupied' ? 'var(--surface)' : 'var(--surface-2)',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                border: table.status === 'available' ? '2px solid var(--ok)' : table.status === 'occupied' ? '2px solid var(--accent)' : '2px solid var(--border-strong)',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                <div>
                  <h4 style={{ margin: '0 0 5px 0', fontSize: '20px', color: 'var(--text)' }}>{table.code}</h4>
                  <p style={{ margin: 0, color: 'var(--muted)', fontSize: '14px' }}>{table.name}</p>
                </div>
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: table.status === 'available' ? 'rgba(63, 191, 127, 0.15)' :
                               table.status === 'occupied' ? 'rgba(239, 91, 91, 0.15)' :
                               table.status === 'reserved' ? 'rgba(245, 181, 68, 0.15)' : 'var(--surface-2)',
                    color: table.status === 'available' ? 'var(--ok)' :
                           table.status === 'occupied' ? 'var(--danger)' :
                           table.status === 'reserved' ? 'var(--warn)' : 'var(--muted)'
                  }}
                >
                  {statuses.find(s => s.value === table.status)?.label || table.status}
                </span>
              </div>

              <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '15px' }}>
                <div>ที่นั่ง: {table.seats} ที่</div>
                <div>พื้นที่: {zones.find(z => z.value === table.zone)?.label || table.zone}</div>

                {/* แสดงจำนวนคนและราคา */}
                {table.guests && table.status === 'occupied' && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    background: 'var(--surface)',
                    borderRadius: '8px',
                    color: 'var(--text)'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                      จำนวนลูกค้า: {(table.guests.adults || 0) + (table.guests.children130 || 0) + (table.guests.children120 || 0) + (table.guests.childrenUnder100 || 0)} คน
                    </div>
                    {table.guests.adults > 0 && (
                      <div style={{ fontSize: '12px' }}>👨 ผู้ใหญ่: {table.guests.adults} คน</div>
                    )}
                    {table.guests.children130 > 0 && (
                      <div style={{ fontSize: '12px' }}>👦 เด็ก {HL.childBig}: {table.guests.children130} คน</div>
                    )}
                    {table.guests.children120 > 0 && (
                      <div style={{ fontSize: '12px' }}>👶 เด็ก {HL.childSmall}: {table.guests.children120} คน</div>
                    )}
                    {table.guests.childrenUnder100 > 0 && (
                      <div style={{ fontSize: '12px' }}>🍼 เด็ก {HL.childFree}: {table.guests.childrenUnder100} คน (ฟรี)</div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: '600', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', color: 'var(--info)' }}>
                      💰 ค่าบุฟเฟ่ต์: {table.pricing?.totalPrice?.toLocaleString() || 0} บาท
                    </div>
                    {tableOrders[table.code] > 0 && (
                      <div style={{ fontSize: '12px', marginTop: '3px', color: 'var(--warn)' }}>
                        🍺 เครื่องดื่ม/อาหารเพิ่ม: +{tableOrders[table.code].toLocaleString()} บาท
                      </div>
                    )}
                    <div style={{ fontSize: '15px', fontWeight: '700', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid rgba(255,255,255,0.2)', color: 'var(--ok)' }}>
                      💵 ยอดรวมทั้งหมด: {((table.pricing?.totalPrice || 0) + (tableOrders[table.code] || 0)).toLocaleString()} บาท
                    </div>
                    {table.startTime && table.endTime && (
                      <div style={{ fontSize: '12px', marginTop: '5px', color: 'var(--info)' }}>
                        🕐 {new Date(table.startTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        {' → '}
                        {new Date(table.endTime).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </div>
                    )}
                    {table.endTime && (
                      <div style={{ fontSize: '12px', marginTop: '3px' }}>
                        ⏰ เหลือเวลา: {(() => {
                          const now = Date.now();
                          const end = new Date(table.endTime).getTime();
                          const remaining = Math.max(0, end - now);
                          const minutes = Math.floor(remaining / 60000);
                          const hours = Math.floor(minutes / 60);
                          const mins = minutes % 60;
                          if (remaining <= 0) return <span style={{ color: 'var(--danger)', fontWeight: '700' }}>หมดเวลา!</span>;
                          return `${hours}:${mins.toString().padStart(2, '0')} ชม.`;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleEdit(table)}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--ok)',
                    color: '#1a1206',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  แก้ไข
                </button>
                <button
                  onClick={() => handleViewQR(table.code)}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--accent)',
                    color: '#1a1206',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  QR Code
                </button>
                {table.status === 'occupied' && (
                  <>
                    <button
                      onClick={() => navigate('/checkout', { state: { selectedTableCode: table.code } })}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--accent)',
                        color: '#1a1206',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      💰 ชำระเงิน
                    </button>
                    {/* รวมบิล — โผล่เมื่อมีโต๊ะอื่นที่มีลูกค้าให้รวมด้วยเท่านั้น */}
                    {tables.some(t => t.status === 'occupied' && t.code !== table.code) && (
                      <button
                        onClick={() => { setMergeSourceCode(table.code); setMergePicks([]); }}
                        style={{
                          padding: '6px 12px',
                          background: 'var(--info)',
                          color: '#1a1206',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}
                      >
                        🧾 รวมบิล
                      </button>
                    )}
                    <button
                      onClick={() => handlePrintBill(table)}
                      disabled={printingTable === table.code}
                      style={{
                        padding: '6px 12px',
                        background: printingTable === table.code ? 'var(--border-strong)' : 'var(--info)',
                        color: printingTable === table.code ? 'var(--muted)' : '#1a1206',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: printingTable === table.code ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      {printingTable === table.code ? '⏳ กำลังพิมพ์...' : '🖨️ พิมพ์ใบเสร็จ'}
                    </button>
                    <button
                      onClick={() => handleClearTable(table.code)}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--warn)',
                        color: '#1a1206',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      🧹 เคลียร์โต๊ะ
                    </button>
                  </>
                )}
                {/* ปุ่มลบถูกซ่อนเพื่อป้องกันการลบโต๊ะโดยไม่ตั้งใจ */}
                {/* <button
                  onClick={() => handleDelete(table.code || table.id)}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--danger)',
                    color: '#1a1206',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  ลบ
                </button> */}
              </div>
            </div>
          ))}
        </div>

        {tables.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
            ยังไม่มีโต๊ะ กรุณาเพิ่มโต๊ะใหม่
          </div>
        )}
      </div>

      {/* Merge Bill Modal — เลือกโต๊ะอื่นที่จะจ่ายพร้อมโต๊ะนี้ */}
      <Modal
        open={!!mergeSource}
        onClose={() => { setMergeSourceCode(null); setMergePicks([]); }}
        title={<>🧾 รวมบิลกับโต๊ะ {mergeSource?.code}</>}
        maxWidth="500px"
      >
        {mergeSource && (() => {
          const candidates = mergeCandidates();
          const picked = candidates.filter(t => mergePicks.includes(t.code));
          const combined = tableGrandTotal(mergeSource) + picked.reduce((sum, t) => sum + tableGrandTotal(t), 0);

          return (
            <div>
              <p style={{ color: 'var(--muted)', marginBottom: '15px', fontSize: '14px' }}>
                เลือกโต๊ะที่จะจ่ายพร้อมกับโต๊ะ {mergeSource.code} — หน้าเช็คบิลจะรวมยอดให้เก็บเงินรอบเดียว
                แต่ยังบันทึกบิลแยกต่อโต๊ะ (รายงานยอดต่อโต๊ะไม่เพี้ยน)
              </p>

              <div style={{
                padding: '12px',
                background: 'var(--surface)',
                borderRadius: '8px',
                marginBottom: '12px',
                border: '2px solid var(--accent)'
              }}>
                <div style={{ fontWeight: '700', color: 'var(--text)' }}>
                  โต๊ะ {mergeSource.code} <span style={{ fontSize: '12px', color: 'var(--info)', fontWeight: '500' }}>(โต๊ะหลัก)</span>
                </div>
                <div style={{ fontSize: '14px', color: 'var(--ok)', fontWeight: '600' }}>
                  {tableGrandTotal(mergeSource).toLocaleString()} บาท
                </div>
              </div>

              {candidates.length === 0 ? (
                <p style={{ color: 'var(--muted)', fontSize: '14px' }}>ไม่มีโต๊ะอื่นที่มีลูกค้าอยู่</p>
              ) : (
                <div style={{ maxHeight: '260px', overflowY: 'auto', marginBottom: '15px' }}>
                  {candidates.map(t => {
                    const checked = mergePicks.includes(t.code);
                    const selectable = canMergeTable(t);
                    return (
                      <label
                        key={t.code}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px',
                          marginBottom: '8px',
                          borderRadius: '8px',
                          background: checked ? 'var(--surface-2)' : 'var(--surface)',
                          border: checked ? '2px solid var(--info)' : '2px solid var(--border-strong)',
                          cursor: selectable ? 'pointer' : 'not-allowed',
                          opacity: selectable ? 1 : 0.5
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!selectable}
                          onChange={() => toggleMergePick(t.code)}
                          style={{ width: '20px', height: '20px', cursor: selectable ? 'pointer' : 'not-allowed', accentColor: 'var(--info)' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: 'var(--text)' }}>โต๊ะ {t.code}</div>
                          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                            {selectable
                              ? `${tableHeadcount(t)} คน`
                              : 'ยังไม่ได้ใส่จำนวนลูกค้า — รวมบิลไม่ได้'}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', color: selectable ? 'var(--ok)' : 'var(--muted)', fontWeight: '600' }}>
                          {tableGrandTotal(t).toLocaleString()} บาท
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <div style={{
                padding: '14px',
                background: 'var(--accent)',
                borderRadius: '8px',
                marginBottom: '15px',
                color: '#1a1206'
              }}>
                <div style={{ fontSize: '13px', opacity: 0.9 }}>
                  ยอดรวม {picked.length + 1} โต๊ะ
                </div>
                <div style={{ fontSize: '26px', fontWeight: '700' }}>
                  {combined.toLocaleString()} บาท
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={confirmMergeBill}
                  disabled={mergePicks.length === 0}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: mergePicks.length === 0 ? 'var(--border-strong)' : 'var(--info)',
                    color: mergePicks.length === 0 ? 'var(--muted)' : '#1a1206',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: mergePicks.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  💰 ไปหน้าเช็คบิลรวม
                </button>
                <button
                  onClick={() => { setMergeSourceCode(null); setMergePicks([]); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'var(--border-strong)',
                    color: 'var(--text)',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Buffet Modal */}
      <Modal
        open={showBuffetModal}
        onClose={() => { setShowBuffetModal(false); setSelectedTable(null); setBuffetData({ adults: 0, childrenBig: 0, childrenSmall: 0 }); }}
        title={<>🍽️ เปิดโต๊ะ {selectedTable?.code}</>}
        maxWidth="500px"
      >

            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'var(--muted)', marginBottom: '15px' }}>กรุณาระบุจำนวนคน:</p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text)' }}>
                  🧑 ผู้ใหญ่ (298 บาท/คน)
                </label>
                <input
                  className="dark-input"
                  type="number"
                  min="0"
                  value={buffetData.adults}
                  onChange={(e) => setBuffetData({...buffetData, adults: parseInt(e.target.value) || 0})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '2px solid var(--border-strong)',
                    borderRadius: '8px',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text)' }}>
                  👦 เด็กโต {HL.childBig} ({BUFFET_PRICES.childBig} บาท/คน)
                </label>
                <input
                  className="dark-input"
                  type="number"
                  min="0"
                  value={buffetData.childrenBig}
                  onChange={(e) => setBuffetData({...buffetData, childrenBig: parseInt(e.target.value) || 0})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '2px solid var(--border-strong)',
                    borderRadius: '8px',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--text)' }}>
                  👶 เด็กเล็ก {HL.childSmall} ({BUFFET_PRICES.childSmall} บาท/คน)
                </label>
                <input
                  className="dark-input"
                  type="number"
                  min="0"
                  value={buffetData.childrenSmall}
                  onChange={(e) => setBuffetData({...buffetData, childrenSmall: parseInt(e.target.value) || 0})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    border: '2px solid var(--border-strong)',
                    borderRadius: '8px',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{
                background: 'var(--surface-2)',
                padding: '15px',
                borderRadius: '8px',
                marginTop: '20px'
              }}>
                <div style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '8px' }}>
                  จำนวนคนทั้งหมด: {buffetData.adults + buffetData.childrenBig + buffetData.childrenSmall} คน
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--ok)' }}>
                  ยอดรวม: {calculateBuffetTotal().toFixed(2)} บาท
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
              <button
                onClick={confirmOpenTable}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--accent)',
                  color: '#1a1206',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ยืนยันเปิดโต๊ะ
              </button>
              <button
                onClick={() => {
                  setShowBuffetModal(false);
                  setSelectedTable(null);
                  setBuffetData({ adults: 0, childrenBig: 0, childrenSmall: 0 });
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--border-strong)',
                  color: 'var(--muted)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                ยกเลิก
              </button>
            </div>
      </Modal>

    </div>
  );
}
